import { HistoryOutlined, SearchOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Empty, Tooltip } from 'antd';
import { ReactComponent as IconBell } from 'assets/images/icons/iconBell.svg';
import { ReactComponent as IconAttachment } from 'assets/images/icons/iconTransactionAttachment.svg';
import {
  DrawerDateRangeFilter,
  useDrawerDateRangeFilter,
} from 'components/Custom/drawer-date-range-filter';
import { RoundInput } from 'components/Custom/inputs';
import { UploadStatusTag } from 'components/Custom/UploadStatusTag';
import { AttachmentViewer } from 'components/Vat/Report/tables/transactions/components/AttachmentViewer/index';
import dayjs from 'dayjs';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getFilesUploadHistoryRequest } from 'store/organisation/actions';
import type { ExcelFileMetadata } from 'store/organisation/models';
import { getFilesUploadHistory } from 'store/organisation/selectors';
import {
  clearMetadataAttachments,
  fetchMetadataAttachmentsRequest,
} from 'store/vat/actions';
import {
  getLastFetchedMetadataAttachments,
  getMetadataAttachmentLoading,
} from 'store/vat/selectors';
import styled from 'styled-components';
import { isUploadDateInRange } from 'utils/drawer-date-range';
import { getUploadStatusTooltip } from 'utils/uploadStatusHelpers';
import { ENTITY_TOOLBAR_MUTED_ICON_COLOR } from './transactions-tab/constants';
import {
  EntityTransactionsToolbarIconButton,
  entityTransactionsToolbarButtonStyle,
} from './transactions-tab/styles';

interface Props {
  connectionId: string;
  /** Render as an icon button (default), full button label, or PWA hero circle. */
  variant?: 'icon' | 'button' | 'circle';
  /** Optional label when using variant="button". */
  buttonLabel?: string;
  /** Disable opening capture history (e.g. prerequisites not met). */
  disabled?: boolean;
  /** Optional class for the root control (circle / icon). */
  className?: string;
}

const ScrollableFileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DriveUploadHistory: React.FC<Props> = ({
  connectionId,
  variant = 'icon',
  buttonLabel,
  disabled = false,
  className,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (!open) return;
    const drawerData: DrawerData = {
      title: t('organisation.data-capture-history'),
      subtitle: t('organisation.processed-documents-log-subtitle'),
      type: DrawerType.DRIVE_UPLOAD_LOGS,
      component: <FileLog />,
    };
    dispatch(turnDrawerOn(drawerData));
    setOpen(false);
  }, [open, dispatch]);

  const openLogHistory = () => {
    if (disabled) return;
    setOpen(true);
    dispatch(getFilesUploadHistoryRequest(connectionId, 'all'));
  };

  const prettySource = (src?: string) => {
    if (!src) return '-';
    if (src === 'ONE_DRIVE') return 'OneDrive';
    if (src === 'GOOGLE_DRIVE') return 'Google Drive';
    if (src === 'MANUAL') return 'Manual Upload';
    if (src === 'EMAIL') return 'Email';
    return String(src);
  };

  const DEFAULT_FILE_LIST_PAGE_SIZE = 8;
  const MIN_FILE_LIST_PAGE_SIZE = 1;
  const FILE_LIST_GAP = 8;
  const FILE_CARD_AVERAGE_HEIGHT = 70;
  const PAGINATION_ROW_FALLBACK_HEIGHT = 28;

  const FileLog: React.FC = () => {
    const logs = useSelector(getFilesUploadHistory);
    const metadataAttachments = useSelector(getLastFetchedMetadataAttachments);
    const metadataAttachmentLoading = useSelector(getMetadataAttachmentLoading);
    const [fileSearchQuery, setFileSearchQuery] = React.useState('');
    const files: ExcelFileMetadata[] = Array.isArray(logs?.files)
      ? logs.files
      : [];

    const openCapturedFilePreview = React.useCallback(
      (log: ExcelFileMetadata) => {
        const fileName = String(log.fileName ?? '').trim() || '-';
        const metadataId = String(log.excelMetadataId ?? '').trim();
        if (!metadataId || !connectionId) return;
        dispatch(
          fetchMetadataAttachmentsRequest(connectionId, metadataId, fileName)
        );
      },
      [connectionId, dispatch]
    );

    React.useEffect(
      () => () => {
        dispatch(clearMetadataAttachments());
      },
      [dispatch]
    );
    const { appliedRange, filterProps } = useDrawerDateRangeFilter({
      enableAutoFallback: true,
      items: files,
    });

    const dateFilteredFiles = React.useMemo(
      () =>
        files.filter((file) =>
          isUploadDateInRange(file.uploadDate, appliedRange)
        ),
      [appliedRange, files]
    );

    const [fileListPageSize, setFileListPageSize] = React.useState(
      DEFAULT_FILE_LIST_PAGE_SIZE
    );
    const [fileListCurrentPage, setFileListCurrentPage] = React.useState(1);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const searchRef = React.useRef<HTMLDivElement | null>(null);
    const cardsViewportRef = React.useRef<HTMLDivElement | null>(null);
    const pagerRef = React.useRef<HTMLDivElement | null>(null);

    const filteredFiles = React.useMemo(() => {
      const q = fileSearchQuery.trim().toLowerCase();
      if (!q) return dateFilteredFiles;
      return dateFilteredFiles.filter((f: { fileName?: string }) =>
        String(f?.fileName ?? '')
          .toLowerCase()
          .includes(q)
      );
    }, [dateFilteredFiles, fileSearchQuery]);

    const fileListTotalPages = Math.max(
      1,
      Math.ceil(filteredFiles.length / fileListPageSize)
    );
    const displayedFiles = filteredFiles.slice(
      (fileListCurrentPage - 1) * fileListPageSize,
      fileListCurrentPage * fileListPageSize
    );

    const updatePageSize = React.useCallback(() => {
      const containerHeight = containerRef.current?.clientHeight ?? 0;
      if (!containerHeight) return;

      const searchHeight = searchRef.current?.offsetHeight ?? 0;
      const cardHeight = FILE_CARD_AVERAGE_HEIGHT;

      const availableHeightWithoutPager = Math.max(
        containerHeight - searchHeight - FILE_LIST_GAP,
        cardHeight
      );
      const tentativePageSize = Math.max(
        MIN_FILE_LIST_PAGE_SIZE,
        Math.floor(
          (availableHeightWithoutPager + FILE_LIST_GAP) /
            (cardHeight + FILE_LIST_GAP)
        )
      );
      const shouldShowPager = filteredFiles.length > tentativePageSize;
      const pagerHeight = shouldShowPager
        ? pagerRef.current?.offsetHeight ?? PAGINATION_ROW_FALLBACK_HEIGHT
        : 0;
      const verticalGaps = shouldShowPager ? FILE_LIST_GAP * 2 : FILE_LIST_GAP;
      const availableCardHeight = Math.max(
        containerHeight - searchHeight - pagerHeight - verticalGaps,
        cardHeight
      );
      const nextPageSize = Math.max(
        MIN_FILE_LIST_PAGE_SIZE,
        Math.floor(
          (availableCardHeight + FILE_LIST_GAP) / (cardHeight + FILE_LIST_GAP)
        )
      );

      setFileListPageSize((currentPageSize) =>
        currentPageSize === nextPageSize ? currentPageSize : nextPageSize
      );
    }, [filteredFiles.length]);

    React.useEffect(() => {
      setFileListCurrentPage(1);
    }, [fileSearchQuery, appliedRange]);

    React.useEffect(() => {
      setFileListCurrentPage((currentPage) =>
        Math.min(currentPage, fileListTotalPages)
      );
    }, [fileListTotalPages]);

    React.useEffect(() => {
      updatePageSize();
    }, [updatePageSize, fileListPageSize]);

    React.useEffect(() => {
      if (typeof window === 'undefined') return undefined;

      const scheduleUpdate = () => {
        window.requestAnimationFrame(updatePageSize);
      };

      const resizeObserver =
        typeof ResizeObserver === 'undefined'
          ? null
          : new ResizeObserver(scheduleUpdate);

      const observedElements = [
        containerRef.current,
        searchRef.current,
        pagerRef.current,
      ].filter(Boolean) as Element[];

      observedElements.forEach((element) => resizeObserver?.observe(element));
      window.addEventListener('resize', scheduleUpdate);

      return () => {
        resizeObserver?.disconnect();
        window.removeEventListener('resize', scheduleUpdate);
      };
    }, [updatePageSize, fileListPageSize]);

    React.useEffect(() => {
      const cardsViewport = cardsViewportRef.current;
      if (!cardsViewport) return;
      cardsViewport.scrollTop = 0;
    }, [fileListCurrentPage, fileSearchQuery, appliedRange]);

    if (files.length === 0) {
      return (
        <div className="drawer-panel-root" style={{ padding: '12px 0' }}>
          <Empty description={t('organisation.no-upload-history-yet')} />
        </div>
      );
    }

    return (
      <>
      <div
        ref={containerRef}
        className="drawer-panel-root"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: FILE_LIST_GAP,
          paddingRight: 4,
        }}
      >
        <div
          ref={searchRef}
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'stretch',
            gap: FILE_LIST_GAP,
            width: '100%',
          }}
        >
          <div
            style={{
              flex: '1 1 0',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <RoundInput
              prefix={
                <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              }
              placeholder={t('organisation.file-filter-search-placeholder')}
              value={fileSearchQuery}
              onChange={(e) => setFileSearchQuery(e.target.value)}
              allowClear
              bg_color="var(--color-surface-raised, #ffffff)"
              width="100%"
              height="34px"
              style={{
                fontSize: 11,
              }}
            />
          </div>
          <DrawerDateRangeFilter
            {...filterProps}
            variant="inline"
            inlineHeight="34px"
          />
        </div>

        {filteredFiles.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-text-muted, #5a6a6a)' }}>
            {fileSearchQuery.trim()
              ? 'No files match your search.'
              : 'No files match the selected date range.'}
          </div>
        )}

        {filteredFiles.length > 0 && (
          <ScrollableFileList
            ref={cardsViewportRef}
            tabIndex={0}
            role="region"
            aria-label={t('organisation.data-capture-history')}
          >
            {displayedFiles.map((log: ExcelFileMetadata) => {
              const statusTooltip = getUploadStatusTooltip(
                log.status,
                log.errorReason
              );
              const statusTag = (
                <UploadStatusTag
                  status={log.status}
                  style={{ cursor: statusTooltip ? 'help' : undefined }}
                />
              );
              const metadataId = String(log.excelMetadataId ?? '').trim();
              const canPreview = !!metadataId && !!connectionId;
              const fileName = log.fileName ?? '-';
              const openLabel = t(
                'organisation.uploaded-file-open-attachment-aria',
                { fileName }
              );

              return (
                <div
                  key={
                    log.excelMetadataId ??
                    `${log.fileName ?? 'file'}-${log.uploadDate ?? ''}`
                  }
                  style={{
                    borderRadius: 16,
                    padding: '10px 12px',
                    border: '2px solid var(--color-border, #dbeae3)',
                    background: 'var(--color-surface-raised, #ffffff)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 10,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#5b6b73',
                        display: 'block',
                      }}
                    >
                      {log.uploadDate
                        ? `Uploaded on: ${dayjs(log.uploadDate).format(
                            'DD/MM/YYYY'
                          )}`
                        : 'Uploaded on: -'}
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      <Tooltip title={openLabel} placement="top">
                        <button
                          type="button"
                          aria-label={openLabel}
                          disabled={!canPreview}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canPreview) {
                              openCapturedFilePreview(log);
                            }
                          }}
                          style={{
                            cursor: canPreview ? 'pointer' : 'default',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            width: 22,
                            height: 22,
                            padding: 0,
                            margin: 0,
                            border: 'none',
                            background: 'transparent',
                            color: 'inherit',
                            opacity: canPreview ? 1 : 0.35,
                          }}
                        >
                          <IconAttachment
                          className="transaction-action-icon"
                            style={{ width: 22, height: 22 }}
                            aria-hidden
                          />
                        </button>
                      </Tooltip>
                      <Tooltip title={log.fileName} placement="top">
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--color-text-dark, #042a2a)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          {fileName}
                        </span>
                      </Tooltip>
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted, #5a6a6a)' }}>
                      Source: {prettySource(log.uploadSource)}
                    </span>
                  </div>
                  {statusTooltip ? (
                    <Tooltip title={statusTooltip} placement="top">
                      <span>{statusTag}</span>
                    </Tooltip>
                  ) : (
                    statusTag
                  )}
                </div>
              );
            })}
          </ScrollableFileList>
        )}

        {filteredFiles.length > fileListPageSize && (
          <div
            ref={pagerRef}
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              flexShrink: 0,
              paddingBottom: 20,
            }}
          >
            <ScopeButton
              width="88px"
              height="25px"
              fontSize="12px"
              lineheight="12px"
              onClick={() => setFileListCurrentPage((p) => Math.max(1, p - 1))}
              type="default"
              style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
              disabled={fileListCurrentPage === 1}
            >
              {t('main.previous')}
            </ScopeButton>
            <ScopeButton
              width="75px"
              height="25px"
              fontSize="12px"
              lineheight="12px"
              onClick={() =>
                setFileListCurrentPage((p) =>
                  Math.min(fileListTotalPages, p + 1)
                )
              }
              type="default"
              disabled={fileListCurrentPage >= fileListTotalPages}
            >
              {t('main.next')}
            </ScopeButton>
          </div>
        )}
      </div>
      {metadataAttachments.metadataId ? (
        <AttachmentViewer
          onClose={() => dispatch(clearMetadataAttachments())}
          transactionReference={metadataAttachments.fileName || ''}
          attachments={metadataAttachments.attachments}
          attachmentLoading={metadataAttachmentLoading}
          providerName=""
          attachmentSourceUrl=""
          showUpload={false}
          standaloneWidth={720}
          standaloneFullHeight
          centered
        />
      ) : null}
      </>
    );
  };

  const tooltipTitle = 'Data Capture History';
  const disabledTooltipTitle =
    'Complete the setup steps to enable Data Capture History.';
  const effectiveTooltipTitle = disabled ? disabledTooltipTitle : tooltipTitle;

  if (variant === 'circle') {
    return (
      <button
        type="button"
        className={['scope-pwa-capture-hero__action', className]
          .filter(Boolean)
          .join(' ')}
        aria-label={effectiveTooltipTitle}
        onClick={openLogHistory}
        disabled={disabled}
      >
        <span className="scope-pwa-capture-hero__action-orb" aria-hidden>
          <HistoryOutlined />
        </span>
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <EntityTransactionsToolbarIconButton
        type="default"
        icon={
          <IconBell
            style={{
              width: 14,
              height: 14,
              color: ENTITY_TOOLBAR_MUTED_ICON_COLOR,
            }}
          />
        }
        style={entityTransactionsToolbarButtonStyle(false)}
        onClick={openLogHistory}
        disabled={disabled}
      >
        {buttonLabel ?? tooltipTitle}
      </EntityTransactionsToolbarIconButton>
    );
  }

  return (
    <Tooltip title={effectiveTooltipTitle}>
      <button
        type="button"
        aria-label={tooltipTitle}
        onClick={openLogHistory}
        disabled={disabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          margin: 0,
          padding: 0,
          border: '1px solid var(--color-border, #dbeae3)',
          borderRadius: 8,
          background: 'var(--color-surface-raised, #ffffff)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: 'var(--color-text-muted, #5a6a6a)',
          lineHeight: 1,
          boxSizing: 'border-box',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <HistoryOutlined style={{ fontSize: 14 }} />
      </button>
    </Tooltip>
  );
};

export default DriveUploadHistory;
